import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { IArea, ILabelledArea, IStateAwareArea } from "types/map";
import { CircleMarker, Tooltip, Tooltip as LeafletTooltip } from "react-leaflet";

import { areaToTooltipData, isInteractable, xy } from "components/Map/util";
import classnames from "classnames";
import InteractiveMarker from 'components/Map/InteractiveMarker';
import { MapModalTooltipContextValue } from 'components/Map/MapModalTooltipContext';

export interface Props extends Pick<MapModalTooltipContextValue, 'openModalTooltip'> {
  area: IStateAwareArea & ILabelledArea,
  currentArea: IArea,
  interactive?: boolean,
  onAreaClick: (e: any, area: IArea) => Promise<void>,
  onAreaSelect: (area?: IArea) => void,
  onTapAtLowZoomLevel: (area: IArea) => void,
  zoomLevel: number,
}

export const AreaMarker: React.FC<Props> = (props) => {
  const {
    area,
    currentArea,
    interactive,
    onAreaClick,
    onAreaSelect,
    onTapAtLowZoomLevel,
    openModalTooltip,
    zoomLevel,
  } = props;

  const ref = useRef<Tooltip>(null);

  const [isTouchActive, setIsTouchActive] = useState(false);
  const [didUserTap, setDidUserTap] = useState(false);

  const onClick = useCallback(() => {
    // Only listen to 'click' events that were fired by taps
    if (!didUserTap) {
      return;
    }

    setDidUserTap(false);

    if (area.shouldZoomOnTapAtZoomLevel(zoomLevel)) {
      onTapAtLowZoomLevel(area);
      return;
    }

    onAreaSelect(area);

    if (area.shouldShowTooltip) {
      const tooltipData = areaToTooltipData(area, currentArea, onAreaClick);
      openModalTooltip({ ...tooltipData });
    }
    return;
  }, [
    area,
    currentArea,
    didUserTap,
    onAreaClick,
    onAreaSelect,
    onTapAtLowZoomLevel,
    openModalTooltip,
    zoomLevel,
  ]);

  const onTouchEnd = useCallback(() => {
    if (isTouchActive) {
      setIsTouchActive(false);
      setDidUserTap(true);
    }
  }, [isTouchActive]);

  const onTouchStart = useCallback(() => {
    // We're touching
    setIsTouchActive(true);
    setDidUserTap(false);
  }, []);

  useEffect(() => {
    if (ref.current) {
      const el = ref.current.leafletElement.getElement()!;

      el.addEventListener('click', onClick, { passive: true });
      el.addEventListener('touchend', onTouchEnd);
      el.addEventListener('touchstart', onTouchStart);

      return (() => {
        el.removeEventListener('click', onClick);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('touchstart', onTouchStart);
      });
    }
  }, [onClick, onTouchEnd, onTouchStart]);

  const {
    labelX,
    labelY,
  } = area;

  const isAreaInteractable = isInteractable(area) && (interactive ?? true);

  return (
    <Fragment>
      <CircleMarker
        center={xy(labelX, labelY)}
        radius={0}
        opacity={0}
      >

        <LeafletTooltip
          className={classnames(
            'leaflet-tooltip--fbg',
            (isAreaInteractable && area.unlocked) ? 'leaflet-tooltip--fbg-interactable' : 'leaflet-tooltip--fbg-landmark',
          )}
          direction="center"
          offset={[0, 0]}
          permanent
          opacity={1}
          ref={ref}
        >
          <InteractiveMarker
            area={area}
            currentArea={currentArea}
            onAreaClick={onAreaClick}
            onAreaSelect={onAreaSelect}
            onTapAtLowZoomLevel={onTapAtLowZoomLevel}
            zoomLevel={zoomLevel}
          />
        </LeafletTooltip>
      </CircleMarker>
    </Fragment>
  );
};

export default AreaMarker;